/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ReferenceObjectData, Scale } from '#utils/UNSAFE_visTypes/chart';
import { Dimension } from '#utils/UNSAFE_visTypes/common';

export function getRefObjectPosition(object: ReferenceObjectData): number | undefined {
  if (object.type === 'line') {
    return object.value;
  }
  if (object.type === 'area') {
    if (object.low != null && object.high != null) {
      return (object.low + object.high) / 2;
    }
  }
  return undefined;
}

export function buildNavigableRefObjects(
  xRefs?: ReferenceObjectData[],
  yRefs?: ReferenceObjectData[],
  y2Refs?: ReferenceObjectData[]
): { id: string; val: number }[] {
  const normalize = (arr?: ReferenceObjectData[]) =>
    (arr ?? [])
      .map((obj) => ({ id: obj.id, val: getRefObjectPosition(obj) }))
      .filter((obj) => obj.id != null && obj.val != null)
      .sort((a, b) => a.val! - b.val!);

  const xList = normalize(xRefs);
  const yList = normalize(yRefs);
  const y2List = normalize(y2Refs);

  return [...xList, ...yList, ...y2List] as { id: string; val: number }[];
}

/**
 * Compute an anchor bounds for a reference object by id. Returns a 1x1 box positioned
 * at a reasonable anchor point for the ref so datatips can use element anchoring.
 */
export function getRefObjectBounds(
  id: string,
  xRefs: ReferenceObjectData[] | undefined,
  yRefs: ReferenceObjectData[] | undefined,
  y2Refs: ReferenceObjectData[] | undefined,
  xScale: Scale | undefined,
  yScale: Scale | undefined,
  y2Scale: Scale | undefined,
  orientation: 'horizontal' | 'vertical',
  plotAreaSpace: Dimension
): Dimension | undefined {
  const ref =
    xRefs?.find((item) => item.id === id) ??
    yRefs?.find((item) => item.id === id) ??
    y2Refs?.find((item) => item.id === id);
  if (!ref) return undefined;

  const isInX = xRefs?.some((item) => item.id === id);
  const isInY2 = y2Refs?.some((item) => item.id === id);

  const isXRef = !!isInX;

  const targetScale = isXRef ? xScale : isInY2 ? y2Scale : yScale;
  const spanningScale = isXRef ? yScale : xScale;

  const asPoint = (cx: number, cy: number): Dimension => ({ x: cx, y: cy, width: 1, height: 1 });

  if (ref.type === 'line' && ref.value != null) {
    const pos = targetScale?.transform(ref.value);
    const range = spanningScale?.range?.();
    const span1 = range?.[0];
    const span2 = range?.[1];
    if (typeof pos !== 'number' || isNaN(pos) || span1 == null || span2 == null) return undefined;

    const mid = (span1 + span2) / 2;
    const isHorizontalLine =
      (orientation === 'vertical' && !isXRef) || (orientation === 'horizontal' && isXRef);
    return isHorizontalLine ? asPoint(mid, pos) : asPoint(pos, mid);
  }

  // Constant area (low/high)
  if (ref.type === 'area') {
    const domain = targetScale?.domain?.();
    const domMin = domain ? Math.min(domain[0], domain[1]) : -Infinity;
    const domMax = domain ? Math.max(domain[0], domain[1]) : Infinity;

    const clampedLow = ref.low != null ? Math.max(ref.low, domMin) : domMin;
    const clampedHigh = ref.high != null ? Math.min(ref.high, domMax) : domMax;

    const pos1 = targetScale?.transform(clampedLow);
    const pos2 = targetScale?.transform(clampedHigh);
    const range = spanningScale?.range?.();
    const span1 = range?.[0];
    const span2 = range?.[1];

    if (
      typeof pos1 !== 'number' ||
      typeof pos2 !== 'number' ||
      isNaN(pos1) ||
      isNaN(pos2) ||
      span1 == null ||
      span2 == null
    ) {
      return undefined;
    }

    const midSpan = (span1 + span2) / 2;
    const midPos = (pos1 + pos2) / 2;
    const isHorizontalArea =
      (orientation === 'vertical' && !isXRef) || (orientation === 'horizontal' && isXRef);
    return isHorizontalArea ? asPoint(midSpan, midPos) : asPoint(midPos, midSpan);
  }

  const centerX = plotAreaSpace.x + plotAreaSpace.width / 2;
  const centerY = plotAreaSpace.y + plotAreaSpace.height / 2;
  return asPoint(centerX, centerY);
}
